import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailCheck, Send, ShieldCheck, Sparkles } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import {
  AuthRequestError,
  forgotPasswordRequest,
  type AuthValidationError,
} from '../../lib/api/auth-client';
import { AuthShell } from './auth-shell';
import { LOGIN_PATH, resolveAuthenticatedPath } from './auth-routing';
import { useAuth } from './auth-context';

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'O email é obrigatório.')
    .email('Introduza um email válido.'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordLocationState {
  email?: string;
}

const recoveryHighlights = [
  {
    icon: MailCheck,
    title: 'Comece pelo seu email',
    description:
      'Indique o email da conta para receber as instruções de recuperação.',
  },
  {
    icon: ShieldCheck,
    title: 'Link enviado com segurança',
    description:
      'Enviamos uma ligação segura para definir uma nova password.',
  },
  {
    icon: Sparkles,
    title: 'Volte a entrar sem complicações',
    description:
      'Depois de redefinir a password, pode regressar rapidamente à app.',
  },
] as const;

const recoveryMetrics = [
  { value: '1 email', label: 'Para começar' },
  { value: '1 link', label: 'Enviado para recuperação' },
  { value: 'Poucos minutos', label: 'Até voltar a entrar' },
] as const;

const fieldClassName =
  'h-14 rounded-[1.35rem] border-[#dfd4c6] bg-[#fffdf9] text-[#16323b] shadow-[0_14px_28px_rgba(50,40,27,0.08)] placeholder:text-[#8c8578] focus-visible:ring-[#0f766e]/25 focus-visible:ring-offset-[#fffaf4]';

const labelClassName =
  'block text-left text-sm font-semibold uppercase tracking-[0.18em] text-[#6d665c]';

function getFieldErrorId(fieldName: keyof ForgotPasswordFormValues) {
  return `${fieldName}-error`;
}

function getForgotPasswordValidationMessage(validationError: AuthValidationError) {
  if (validationError.field !== 'email') {
    return null;
  }

  if (validationError.errorType === 'blank') {
    return 'Indique o email.';
  }

  return 'Introduza um email válido.';
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, session } = useAuth();
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as ForgotPasswordLocationState)
      : undefined;

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email: locationState?.email ?? '',
    },
  });

  const currentEmail = watch('email');

  React.useEffect(() => {
    if (isAuthenticated && session) {
      navigate(resolveAuthenticatedPath(session), { replace: true });
    }
  }, [isAuthenticated, navigate, session]);

  function handleRequestError(error: unknown) {
    if (error instanceof AuthRequestError && error.validationError) {
      const fieldMessage = getForgotPasswordValidationMessage(error.validationError);

      if (fieldMessage) {
        setError('email', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }
    }

    setError('root.server', {
      type: 'server',
      message:
        error instanceof Error
          ? error.message
          : 'Não foi possível iniciar a recuperação de password.',
    });
  }

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();
    setSuccessMessage(null);

    try {
      await forgotPasswordRequest({
        email: values.email,
      });
      setSuccessMessage(
        'Se este email existir, enviámos instruções para redefinir a password.'
      );
    } catch (error) {
      handleRequestError(error);
    }
  });

  return (
    <AuthShell
      heroBadge="Recuperar acesso"
      heroTitle="Recupere o acesso à sua conta."
      heroDescription="Indique o email associado à conta e enviamos um link para definir uma nova password com segurança."
      heroHighlights={recoveryHighlights}
      heroMetrics={recoveryMetrics}
      formBadge="Password"
      formTitle="Recuperar password"
      formDescription="Usamos este email para localizar a conta e enviar as instruções de recuperação."
    >
      <form className="space-y-6" onSubmit={onSubmit} noValidate>
        {successMessage ? (
          <div
            className="rounded-[1.5rem] border border-[#cae7dc] bg-[#ebf7f1] px-4 py-4 text-sm font-medium text-[#0f766e]"
            role="status"
          >
            {successMessage}
          </div>
        ) : null}

        <div className="space-y-3">
          <label className={labelClassName} htmlFor="email">
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            autoComplete="email"
            className={cn(
              fieldClassName,
              errors.email && 'border-destructive/60 bg-destructive/5'
            )}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? getFieldErrorId('email') : undefined}
            {...register('email', {
              onChange: () => {
                clearErrors('root.server');
                setSuccessMessage(null);
              },
            })}
          />
          {errors.email ? (
            <p
              id={getFieldErrorId('email')}
              className="px-2 text-left text-sm font-medium text-destructive"
            >
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <div className="rounded-[1.5rem] border border-[#efe4d7] bg-[#fff7ef]/80 p-4 text-sm leading-6 text-[#5f655f]">
          Vamos usar <strong>{currentEmail || 'o email indicado'}</strong> para enviar
          as instruções de recuperação.
        </div>

        {errors.root?.server?.message ? (
          <div
            className="rounded-[1.5rem] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
            role="alert"
          >
            {errors.root.server.message}
          </div>
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="w-full rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
          disabled={isSubmitting}
        >
          <Send className="mr-2 h-5 w-5" />
          {isSubmitting ? 'A enviar...' : 'Enviar instruções'}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full rounded-[1.35rem] border border-[#e5d8ca] bg-[#fff7ef] text-[1.02rem] text-[#16323b] hover:bg-[#f8efe3]"
          onClick={() =>
            navigate(LOGIN_PATH, {
              replace: true,
              state: {
                email: currentEmail,
              },
            })
          }
        >
          Voltar ao login
        </Button>
      </form>
    </AuthShell>
  );
}
